"use client";
import { Link, PostMeta } from "@areahub/ui";
import {
  Box,
  Flex,
  Heading,
  SimpleGrid,
  useBreakpointValue,
  Text,
} from "@chakra-ui/react";
import { ButtonProp, ImageFragment } from "lib/gcms";
import Image from "next/legacy/image";
import { SectionTemplate } from "../section-template";
import { Button, ButtonActions } from "@ui/components/button";
import { Gcms_ButtonTheme } from "lib/cms-typings";

interface TeaserCardProps {
  /**
   * Title
   */
  displayName: string;
  /**
   * Post Image
   */
  image: ImageFragment & { alt?: string };
  /**
   * URL link associated with the post page
   */
  link?: string;
  /**
   * the slug and title object associated with the post's category (i.e. Pollution)
   */
  aggregatePostPage?: {
    title: string;
    slug: string;
  };
  /**
   * CTA button
   */
  button?: ButtonProp;
}

export interface TeaserCardsSectionProps {
  title: string;
  subtitle: string;
  bg: string;
  color: string;
  teaserCards?: TeaserCardProps[];
  button?: ButtonProp;
}

export function TeaserCardsSection({
  title,
  subtitle,
  bg,
  color,
  teaserCards,
  button,
}: TeaserCardsSectionProps) {
  return (
    <SectionTemplate {...{ bg, color }}>
      <Box textAlign="center" px={{ base: "6", md: "8" }}>
        <Heading
          as="h2"
          fontWeight="bold"
          fontSize={{
            base: 26,
            md: 34,
            lg: 38,
            xl: 45,
          }}
          lineHeight={1.2}
        >
          {title}
        </Heading>
        <Text
          fontSize={{
            base: "14px",
            md: "16px",
            lg: "17px",
            xl: "18px",
          }}
          fontWeight="normal"
          mt="20px"
          textAlign={"left"}
        >
          {subtitle}
        </Text>
        <SimpleGrid
          columns={{ lg: 4, md: 2, base: 1 }}
          spacing="10"
          mt="48px"
          mb="36px"
        >
          {teaserCards?.map((card, idx) => (
            <TeaserCard key={idx} {...(card as TeaserCardProps)} />
          ))}
        </SimpleGrid>
        {button && (
          <Button
            __type={button.buttonAction as ButtonActions}
            href={button.href as string}
            label={button.label}
            borderColor="black"
            borderWidth={1}
            bgColor={button?.theme as Gcms_ButtonTheme}
            mt="4"
            py={{
              base: "6",
              lg: "8",
            }}
            px="5"
          />
        )}
      </Box>
    </SectionTemplate>
  );
}

function TeaserCard({
  displayName,
  link,
  image,
  aggregatePostPage,
}: TeaserCardProps) {
  return (
    <Link
      href={link as string}
      _hover={{
        transform: "translateY(-10px) scale(1.05)",
        textDecoration: "none",
      }}
    >
      <TeaserCardContent
        image={image}
        link={link as string}
        displayName={displayName}
        aggregatePostPage={aggregatePostPage}
      />
    </Link>
  );
}

function TeaserCardContent({
  displayName,
  image,
  aggregatePostPage,
}: TeaserCardProps) {
  const size = useBreakpointValue({
    base: "80vw",
    md: "30vw",
    lg: "20vw",
  });
  return (
    <Flex
      flexDir={"column"}
      justify={"space-between"}
      h="100%"
      shadow="lg"
      color="blueGray.800"
      borderRadius="10px"
      borderWidth="1px"
      px="16px"
      pb={"24px"}
      pt={"16px"}
      bg="white"
    >
      <Box>
        {/* Post Preview Image */}
        <Box display="block" width={"auto"} mx={"auto"} pb={5}>
          <Image
            {...image}
            className="post-media"
            objectFit="contain"
            sizes={size}
            src={image.url}
            width={608}
            height={320}
            placeholder={
              typeof image?.blurDataURL === "string" ? "blur" : undefined
            }
            blurDataURL={
              typeof image?.blurDataURL === "string"
                ? image?.blurDataURL
                : undefined
            }
            alt={image?.alt}
          />
        </Box>
        {/* Post Category Tag */}
        {aggregatePostPage && (
          <PostMeta
            type={aggregatePostPage?.title as string}
            url={`/knowledge-center/category/${aggregatePostPage?.slug}`}
          />
        )}
        {/* Post Title */}
        <Heading
          as="h3"
          fontSize={{
            base: "lg",
            lg: "lg",
          }}
          lineHeight={"1.5"}
          mt="10px"
          fontWeight="bold"
          textAlign={"left"}
        >
          {displayName}
        </Heading>
      </Box>
    </Flex>
  );
}
